import { useRef, useState, useEffect, useContext } from 'react'
import axios, { AxiosResponse } from 'axios'
import moment, { Moment } from '@e3dc-react/shell/Libs/moment'
import { FetchProps, useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import { Breakthrough as BreakthroughModel } from '../../../Types/responses/storages/serialnumber/forecast/breakthrough'
import { ChartView, RechartData } from '../../chart-kit/shared/utils/chartModel'
import { updateChartData } from '../systemDataHandler'
import { getFetchRanges, TimeRange } from '../utils/datetime'
import { ForecastPowerResponse } from '../../../Types/responses/storages/serialnumber/forecast/power'
import { WallboxHistory } from '../../../Types/responses/wallboxes/{sn}'
import { PowermeterHistory } from '../../../Types/responses/powermeter-values/{sn}'
import { getBinarySearchCutoffIndex, ONE_MINUTE_MS } from '../utils/helperFunctions'
import {
  StoreType,
  SystemDataStoreAction,
  SystemDataStoreActionWithTime,
  VersionStateAction,
  getPersistor,
  getStore,
  updateAppVersion,
} from '../state/store'
import { Breakthrough, HistoryData, SystemInfo, TemperatureData } from '../models/systemDataStoreModel'
import { deepEqual } from 'fast-equals'
import { fetchHistoryData, fetchPowermeterHistoryData, fetchTemperatureData, fetchWallboxHistoryData } from './historyDataFetching'
import { transformBreakthroughData, transformForecastData, transformExtendedHistoryData, transformHistoryData } from '../utils/transformers'
import { binarySearchObject } from '../utils/binarySearch'
import { useTypedSelector } from 'src/State/RootReducer'
import { resetStoreHistoryData } from 'src/Libs/system-data-handler/state/store'
import { version as currentAppVersion } from '../../../../package.json'
import { useStorageIdList as getStorageList } from '../../summary-data/hooks/use-storage-id-to-get-list'
import { isEmpty } from 'lodash'
import { usePrevious } from '@e3dc-react/shell/Hooks'
import { DynamicPowerPricesEnabledContext } from '../../dynamic-power-prices/context/dynamic-power-prices-enabled-context'
import { HistoryDataFetchType, HistoryDataToFetch } from '../types'
import { Store } from 'redux'
import { useThreeSixtySavingsConsumption } from 'src/Libs/dynamic-power-prices/hooks/use-three-sixty-savings-consumption'
import useSystemInfoTanQuery from '../hooks/use-system-info'
import { fetchBreakthrough, fetchForecast } from './data-fetching'
import { useFarmContext } from 'src/Elements/Farm'

type AuthFetch = <T>(props: FetchProps) => Promise<AxiosResponse<T>>

export const useSystemInfoStorage = (serialNo: string | undefined): { systemInfo: SystemInfo | null; isLoading: boolean } => {
  const oldStore = useRef<unknown>()
  const { systemInfo: systemInfoTanQuery, isSystemInfoLoading } = useSystemInfoTanQuery(serialNo)

  useEffect(() => {
    if (serialNo && systemInfoTanQuery && !isSystemInfoLoading) {
      if (getStore()?.getState().systemInfo[serialNo] === undefined) {
        getStore()?.dispatch({ type: 'systemInfo/fetched', systemInfo: systemInfoTanQuery, serialNo })
      }

      return getStore()?.subscribe(() => {
        if (oldStore.current === getStore()?.getState()) {
          return
        }
        oldStore.current = getStore()?.getState()
      })
    }
  }, [serialNo, systemInfoTanQuery, isSystemInfoLoading])

  return { systemInfo: systemInfoTanQuery, isLoading: isSystemInfoLoading }
}

export const useFetchData = ({
  storageId,
  from,
  to,
  view,
  dataSet = HistoryDataToFetch.ExtendedSet,
}: {
  storageId: string | undefined
  from: Moment | undefined
  to: Moment | undefined
  view: ChartView
  dataSet?: HistoryDataFetchType
}): RechartData => {
  const oldStore = useRef<unknown>()
  const authFetch = useAuthFetchWithResponse()
  const [data, setData] = useState<RechartData>({ loadingState: { state: 0 } })
  const storeInitialised = useTypedSelector(state => state.storage.storeInitialised)
  const installationsTimezone = useTypedSelector(state => state.storage.installation?.timezone)
  const [date, setDate] = useState<number>(Date.now())
  const storagesList: string[] = getStorageList(storageId)
  const resolution = view === 'year' || view === 'month' ? 'month' : 'day'
  const threeSixtySavingsTotalConsumption = useThreeSixtySavingsConsumption({ storagesIds: storagesList, from, to, resolution })
  const { isDynamicPowerPriceEnabled } = useContext(DynamicPowerPricesEnabledContext)
  const { farmMaster, isLoading: isFarmLoading } = useFarmContext()
  const { systemInfo } = useSystemInfoStorage(storageId)

  /**
   * Chart Refresh occurs every 60 minutes or when user refreshes it. Then it takes up to one minute to start refetching the data.
   * The refetchHistoryData value helps to know if that happened and start fetching the data directly.
   */
  const historyData = getStore()?.getState().historyData
  const previousHistoryData = usePrevious(historyData)
  const refetchHistoryData = isEmpty(historyData) && !isEmpty(previousHistoryData)

  useEffect(() => {
    const intervalId = setInterval(() => setDate(Date.now()), ONE_MINUTE_MS)
    return () => clearInterval(intervalId)
  }, [])

  useEffect(
    () => {
      if (storageId && from && to && storeInitialised && !isFarmLoading) {
        const controller = new AbortController()
        const { signal } = controller

        const fetch = async (): Promise<void> => {
          await clearCacheIfAppVersionChanged()

          if (systemInfo) {
            const ranges = getFetchRanges(from, to)

            for (const range of ranges) {
              if (dataSet === HistoryDataToFetch.ExtendedSet) {
                await fetchExtendedHistoryDataForRange(systemInfo, range, authFetch, storageId, signal, installationsTimezone)
              }
              if (dataSet === HistoryDataToFetch.DefaultSet) {
                await fetchHistoryDataForRange(systemInfo, range, authFetch, storageId, signal)
              }
            }
          }
        }

        fetch()
          .then(() => {
            const updatedChartData = updateChartData({
              serialNo: storageId,
              localFrom: from,
              localTo: to,
              view,
              threeSixtySavingsTotalConsumption,
              isDynamicPowerPriceEnabled,
            })

            setData(updatedChartData)
          })
          .catch(err => {
            if (!axios.isCancel(err)) {
              console.error('Error fetching history data:', err)
            }
          })

        const unsubscribe = getStore()?.subscribe(() => {
          if (oldStore.current === getStore()?.getState()) {
            return
          }

          const newData = updateChartData({
            serialNo: storageId,
            localFrom: from,
            localTo: to,
            view,
            threeSixtySavingsTotalConsumption,
            isDynamicPowerPriceEnabled,
          })
          setData(data => (!deepEqual(data, newData) ? newData : data))

          oldStore.current = getStore()?.getState()
        })

        return () => {
          controller.abort()
          unsubscribe?.()
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      storageId,
      from,
      to,
      view,
      storeInitialised,
      isDynamicPowerPriceEnabled,
      date,
      threeSixtySavingsTotalConsumption,
      refetchHistoryData,
      dataSet,
      isFarmLoading,
      farmMaster,
      systemInfo,
    ]
  )

  return data
}

/**
 * Fetches history data and additional data for a given time range to be displayed in a chart.
 * This includes: history data, wallboxData, powermeterData, temperatureData, breakthroughData, forecastPowerData
 *
 * Error Handling: Each data fetch has its own error handling to prevent unhandled rejections.
 */
const fetchExtendedHistoryDataForRange = async (
  systemInfo: SystemInfo,
  range: TimeRange,
  authFetch: AuthFetch,
  storageId?: string,
  signal?: AbortSignal,
  installationsTimezone?: string
): Promise<void> => {
  const store = getStore()

  if (!store) {
    console.error('store was not initialised and history data could not be fetched')
    return
  }

  const { correctedFrom, correctedTo, doFetchData } = determineFetchRangeAndStatus(store, systemInfo, range, storageId)

  if (!doFetchData) return

  const { from } = range
  const { serialNo } = systemInfo

  store.dispatch({ type: 'history/fetching', serialNo, from: from })

  try {
    const historyDataPromise = fetchHistoryData(systemInfo, correctedFrom, correctedTo, authFetch, signal)
    const temperatureDataPromise = fetchTemperatureData(systemInfo, correctedFrom, correctedTo, authFetch, signal).catch(err => {
      console.error('Error fetching temperature data:', err)
      return [] as TemperatureData[]
    })

    const availableWallboxes = systemInfo.wallboxes.wallboxes
    const wallboxHistoryPromises = availableWallboxes.map(wallbox =>
      fetchWallboxHistoryData(systemInfo, correctedFrom, correctedTo, wallbox.number, wallbox.id, authFetch, signal).catch(err => {
        console.error('Error fetching wallbox history data:', err)
        return null
      })
    )

    const availablePowermeters = systemInfo.powermeters.powermeters
    const powermeterHistoryPromises = availablePowermeters.map(powermeter =>
      fetchPowermeterHistoryData(systemInfo, correctedFrom, correctedTo, powermeter.id, authFetch, powermeter.storageId?.toString(), signal).catch(
        err => {
          console.error('Error fetching powermeter history data:', err)
          return null
        }
      )
    )

    const breakthroughDataPromise = fetchBreakthrough(systemInfo.serialNo, correctedFrom, correctedTo, authFetch, signal)
    const forecastPowerPromise = fetchForecast(systemInfo.serialNo, correctedFrom, correctedTo, authFetch, signal)

    const historyData = await historyDataPromise
    const temperatureData = await temperatureDataPromise
    const breakthroughData = await breakthroughDataPromise
    const forecastPower = await forecastPowerPromise
    const wallboxHistory = (await Promise.all(wallboxHistoryPromises)).filter(wallboxHistory => wallboxHistory !== null)
    const powermeterHistory = (await Promise.all(powermeterHistoryPromises)).filter(powermeterHistory => powermeterHistory !== null)

    const wallboxHistoryData: Record<number, WallboxHistory[]> = {}

    wallboxHistory.forEach(wbData => {
      /** Do not show wallbox values before the first timestamp */
      const wallboxFirstTimestamp = systemInfo.wallboxes.wallboxes[wbData.id]?.info.first
      const cutoffIndex = wallboxFirstTimestamp ? getBinarySearchCutoffIndex(wbData.data, 'time', wallboxFirstTimestamp) : 0
      wallboxHistoryData[wbData.id] = cutoffIndex > 0 ? wbData.data.slice(cutoffIndex) : wbData.data
    })

    const powermeterHistoryData: Record<string, PowermeterHistory[]> = {}
    powermeterHistory.forEach(pm => {
      powermeterHistoryData[pm.uniqueId] = pm.data
    })

    const fetchedTimestamps = historyData.map(h => h.time)
    const fetchedLast = Math.max(Number.MIN_VALUE, ...fetchedTimestamps)

    /**
     * If the month being fetched is the current month, we should not mark it as completed
     * even if there is no data currently to be fetched
     */
    const currentMonthIsBeingFetched = moment(from).startOf('month').isSame(moment().startOf('month'))
    const completed = currentMonthIsBeingFetched ? false : fetchedLast <= systemInfo.history.info.last

    setExtendedHistoryDataToStore(
      breakthroughData,
      forecastPower,
      historyData,
      temperatureData,
      wallboxHistoryData,
      powermeterHistoryData,
      completed,
      serialNo,
      correctedFrom,
      installationsTimezone
    )
  } catch (error) {
    if (!axios.isCancel(error)) {
      store.dispatch({ type: 'error', serialNo, error: error as Error })
    }
  }
}

/**
 * Fetches history data for a given time range
 */
const fetchHistoryDataForRange = async (
  systemInfo: SystemInfo,
  range: TimeRange,
  authFetch: AuthFetch,
  storageId?: string,
  signal?: AbortSignal
): Promise<void> => {
  const store = getStore()

  if (!store) {
    console.error('store was not initialised and history data could not be fetched')
    return
  }

  const { correctedFrom, correctedTo, doFetchData } = determineFetchRangeAndStatus(store, systemInfo, range, storageId)

  if (!doFetchData) return

  const { from } = range
  const { serialNo } = systemInfo

  store.dispatch({ type: 'history/fetching', serialNo, from: from })

  try {
    const historyData = await fetchHistoryData(systemInfo, correctedFrom, correctedTo, authFetch, signal)

    const fetchedTimestamps = historyData.map(h => h.time)
    const fetchedLast = Math.max(Number.MIN_VALUE, ...fetchedTimestamps)

    /**
     * If the month being fetched is the current month, we should not mark it as completed
     * even if there is no data currently to be fetched
     */
    const currentMonthIsBeingFetched = moment(from).startOf('month').isSame(moment().startOf('month'))
    const completed = currentMonthIsBeingFetched ? false : fetchedLast <= systemInfo.history.info.last
    setHistoryDataToStore(historyData, completed, serialNo, correctedFrom)
  } catch (error) {
    if (!axios.isCancel(error)) {
      store.dispatch({ type: 'error', serialNo, error: error as Error })
    }
  }
}

/**
 * Takes a time range and determines if data needs to be fetched or is already present
 */
const determineFetchRangeAndStatus = (
  store: Store<StoreType, SystemDataStoreAction | SystemDataStoreActionWithTime | VersionStateAction>,
  systemInfo: SystemInfo,
  range: TimeRange,
  storageId?: string
): { correctedTo: number; correctedFrom: number; doFetchData: boolean } => {
  const { from, to } = range
  const correctedTo = moment(to).subtract(15, 'minutes').valueOf() //don't include 00:00 as endpoint cause it is the startpoint of a day already
  let correctedFrom = from
  const { serialNo } = systemInfo
  const historyDataFrom = store.getState().historyData[serialNo]?.[from]
  const historyDataTo = store.getState().historyData[serialNo]?.[to]

  const historyDataFromCompleted = historyDataFrom?.completed
  const hasLastTimestampData = binarySearchObject(historyDataFrom?.data, systemInfo?.history.info.last, 'time')

  /* Check if last timestamp in history data is older than 60 mins from current time */
  const SIXTY_MINUTES_MS = 60 * 60 * 1000
  const isLastTimestampOld = Date.now() - Date.parse(historyDataTo?.last || '') > SIXTY_MINUTES_MS

  /* Check if 60 mins have past since the last resetStoreHistoryData call */
  const lastHistoryResetTime = localStorage.getItem('lastHistoryResetTime')
  const isResetAllowed = Date.now() - Number(lastHistoryResetTime) > SIXTY_MINUTES_MS

  if (isLastTimestampOld && storageId && isResetAllowed) {
    resetStoreHistoryData(storageId)
    localStorage.setItem('lastHistoryResetTime', Date.now().toString())
  }

  if (historyDataFromCompleted || hasLastTimestampData) {
    return { correctedFrom, correctedTo, doFetchData: false }
  }

  if (historyDataFrom !== undefined && !historyDataFrom?.completed && historyDataFrom.last) {
    const lastAvailableTs = historyDataFrom?.last
    const lastCompletedTs = moment(lastAvailableTs).subtract(1, 'day').startOf('day')
    correctedFrom = lastCompletedTs.valueOf()
  }

  return { correctedTo, correctedFrom, doFetchData: true }
}

/**
 * Stores history and additional data in redux
 */
const setExtendedHistoryDataToStore = (
  breakthroughData: BreakthroughModel[],
  forecastData: ForecastPowerResponse[],
  historyData: HistoryData[],
  temperatureData: TemperatureData[],
  wallboxHistory: Record<number, WallboxHistory[]>,
  powermeterHistory: Record<string, PowermeterHistory[]>,
  completed: boolean,
  serialNo: string,
  from: number,
  installationsTimezone?: string
): void => {
  const breakthroughDataTransformed: Breakthrough[] = transformBreakthroughData(breakthroughData, installationsTimezone)

  const forecastPowerTransformed = transformForecastData(forecastData)

  const historyTransformed = transformExtendedHistoryData(
    historyData,
    temperatureData,
    wallboxHistory,
    powermeterHistory,
    breakthroughDataTransformed
  )

  getStore()?.dispatch({
    type: 'fetched/historyData/extendedSet',
    forecastPower: forecastPowerTransformed,
    historyData: historyTransformed.history,
    breakthrough: breakthroughDataTransformed,
    wallboxes: historyTransformed.wallboxHistory,
    powermeters: historyTransformed.powermeterHistory,
    serialNo,
    completed,
    from: from,
  })
}

/**
 * Stores history data in redux
 */
const setHistoryDataToStore = (historyData: HistoryData[], completed: boolean, serialNo: string, from: number): void => {
  const historyTransformed = transformHistoryData(historyData)

  getStore()?.dispatch({
    type: 'fetched/historyData/defaultSet',
    historyData: historyTransformed.history,
    serialNo,
    completed,
    from: from,
  })
}

const clearCacheIfAppVersionChanged = async (): Promise<void> => {
  const store = getStore()
  if (!store) return

  const lastKnownAppVersion = store.getState().versionInfo.version

  if (lastKnownAppVersion !== currentAppVersion) {
    if (lastKnownAppVersion !== undefined) {
      await getPersistor()?.purge()
    }
    store.dispatch(updateAppVersion(currentAppVersion))
  }
}
